import { DatePipe, NgClass, NgFor, NgIf } from '@angular/common';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PrimeTemplate } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { ApiService } from 'src/app/core/services/api.service';
import { ConfigurationService } from 'src/app/core/services/configuration.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { BelenderStatusCodes } from 'src/app/models/BelenderStatusCodes';
import { BelenderWebShocketMessage } from 'src/app/models/BelenderWebShocketMessage';
import { LabeledValue } from 'src/app/models/LabeledValue';
import { NewRequestStatus } from 'src/app/models/NewRequestStatus';
import { PhysicalPersonQueryResponse } from 'src/app/models/OpenData.PhysicalPersonQueryResponse';
import { AvailableQueries, DocumentationPack } from 'src/app/models/Security.User';
import { AdminMessagesComponent } from 'src/app/shared/components/admin-messages/admin-messages.component';
import { QrCodeComponent } from 'src/app/shared/components/qr-code/qr-code.component';
import { DNI, INVALID, SEPARATOR } from 'src/app/shared/constants/validation.constants';
import { UtilsService } from 'src/app/utils/utils.service';
import { environment } from 'src/environments/environment';
import { InsideDisableComponent } from '../../../utils/inside-disable/inside-disable.component';
import { LoadingComponent } from '../../../utils/loading/loading.component';
import { FreelancerRequestService } from '../freelancer-requests/freelancer-request.service';
import { NewFreelancerRequestService } from './new-freelancer-request.service';

@Component({
    selector: 'app-new-freelancer-request',
    templateUrl: './new-freelancer-request.component.html',
    styleUrl: './new-freelancer-request.component.scss',
    standalone: true,
    imports: [
        ToastModule,
        PrimeTemplate,
        NgFor,
        ConfirmDialogModule,
        NgClass,
        NgIf,
        RadioButtonModule,
        FormsModule,
        TooltipModule,
        LoadingComponent,
        InsideDisableComponent,
        ReactiveFormsModule,
        InputTextModule,
        DatePickerModule,
        SelectModule,
        DatePipe,
        TranslateModule,
        AdminMessagesComponent,
        QrCodeComponent
    ],
})
export class NewFreelancerRequestComponent implements OnInit {

    @ViewChild('adminMessages') adminMessagesComponent: AdminMessagesComponent;

    physicalPersonQueryResponse: PhysicalPersonQueryResponse;

	@Input() newRequestStatus: NewRequestStatus = { title: 'New request', step: 1 };
	@Input() trustedConsentUrlBoxed: any;

	isLoading = false;
	formIsDisabled = false;

	documentationPackValues: DocumentationPack[] = [];
	private readonly waitingMessagesTranslationKeys = ['NEW_REQUEST.WAITING_MESSAGES.WAIT_1', 'NEW_REQUEST.WAITING_MESSAGES.WAIT_2', 'NEW_REQUEST.WAITING_MESSAGES.WAIT_3'];
	waitingMessages = [];

	private invalidFields = [];
	private requiredFieldsMissing = false;

	selectedRequestorType = '';
	selectedRequestType = '';

	availableQueries: AvailableQueries;

	@Input() belenderStatus: {
		actualStatus: string;
		actualStatusMessage: string;
	} = { actualStatus: '', actualStatusMessage: '' };

	isLoadingRequest = false;

	isAdminOk = true;

	belenderWidgetLangOptions: LabeledValue[] = [
	    { label: 'LANGUAGES.ES', value: 'es' },
	    { label: 'LANGUAGES.CA', value: 'ca' }
	];

	constructor(
		private readonly api: ApiService,
		private readonly router: Router,
		private readonly translateService: TranslateService,
		private readonly toastService: ToastService,
		public readonly freelancerRequestService: FreelancerRequestService,
		public readonly newFreelancerRequestService: NewFreelancerRequestService,
		private readonly sanitizer: DomSanitizer,
		private readonly configurationService: ConfigurationService,
        private readonly utilsService: UtilsService
	) { }

	ngOnInit(): void {
	    window.addEventListener("message", this.receiveMessage, false);
	    this.waitingMessages = this.waitingMessagesTranslationKeys.map(key => this.translateService.instant(key));
	    this.documentationPackValues = this.configurationService.getConfiguration().documentationPacks;
	    this.availableQueries = this.configurationService.getConfiguration().availableQueries;
	    this.newFreelancerRequestService.resetRequestorAndRequestTypes();
	}

	receiveMessage = (event: MessageEvent) => {
	    const messageFromBelender = event.data as BelenderWebShocketMessage;

	    if (messageFromBelender && messageFromBelender.type == "websocket") {
	        if (BelenderStatusCodes.includes(messageFromBelender.data.status_code)) {
	            // Assert form está deshabilitado
	            if (!this.formIsDisabled) this.formIsDisabled = true;

	            this.belenderStatus.actualStatus = messageFromBelender.data.status_code;
	            this.belenderStatus.actualStatusMessage = messageFromBelender.data.status_message;

	            if (this.belenderStatus.actualStatus === "consent_process_completed") {
	                this.toastService.showSuccess('TOAST.SUCCESS', 'NEW_REQUEST.CREATED', true);
	            }
	        }
	    }
	};

	detailButtonIsVisible(): boolean {
	    return this.belenderStatus.actualStatus === "consent_process_completed";
	};

	updateTrustedUrl(newUrl: string): void {
	    this.trustedConsentUrlBoxed = this.sanitizer.bypassSecurityTrustResourceUrl(newUrl);
	}

	goToDetail(): void {
	    if (this.physicalPersonQueryResponse) {
	        this.isLoadingRequest = true;
	        this.router.navigate([`/freelancers/request-detail/${this.physicalPersonQueryResponse.queryId}`])
	            .catch(error => { console.error(error); })
	            .finally(() => this.isLoadingRequest = true);
	    }
	}

	goToStepOne(): void {
	    this.newRequestStatus = { title: 'New request', step: 1 };
	    this.newFreelancerRequestService.resetSidebar();
	}

	goToStepTwo(): void {
	    if (this.selectedRequestorType != '' && this.selectedRequestType != '') {
	        this.newRequestStatus = { title: 'Request data', step: 2 };
	        this.newFreelancerRequestService.setForm(this.selectedRequestorType, this.selectedRequestType);
	    }
	}

	//the validation is moved to the confirmSubmit method
	async onSubmit(): Promise<void> {
	    this.formIsDisabled = true;

	    if (this.newFreelancerRequestService.myForm.valid) {
	        try {
	            this.isLoading = true;
	            const _myFormCopy = { ...this.newFreelancerRequestService.myForm.value };

	            // if (this.newFreelancerRequestService.myForm.get('birthDate')) {
	            //     // modifying the birthDate to the correct format
	            //     const _birthDate = new Date(this.newFreelancerRequestService.myForm.value.birthDate);
	            //     const _formattedBirthDate = this.utilsService.formatLocalYYYYMMDD(_birthDate);
	            //     _myFormCopy.birthDate = _formattedBirthDate;
	            // }

	            // if (this.newFreelancerRequestService.myForm.get('personIdExpirationDate')) {
	            //     // modifying the personIdExpirationDate to the correct format
	            //     const _personIdExpirationDate = new Date(this.newFreelancerRequestService.myForm.value.personIdExpirationDate);
	            //     const _formattedPersonIdExpirationDate = this.utilsService.formatLocalYYYYMMDD(_personIdExpirationDate);
	            //     _myFormCopy.personIdExpirationDate = _formattedPersonIdExpirationDate;
	            // }

	            // TEMPORAL - REPLACEMENT FOR EMPTY SECOND SURNAME
	            if (this.utilsService.isStringNullOrEmpty(_myFormCopy.secondSurname)){
	                _myFormCopy.secondSurname = '-';
	            }

	            _myFormCopy.personId = _myFormCopy.personId.toUpperCase();
	            _myFormCopy.belenderCircuit = this.newFreelancerRequestService.selectedBelenderCircuit;
	            _myFormCopy.clientEnvironment = this.configurationService.getConfiguration().clientEnviroment;
	            _myFormCopy.sendLink = this.getSendLink();
	            this.physicalPersonQueryResponse = await this.api.post<PhysicalPersonQueryResponse>('requests/new-request', _myFormCopy);

	            if (!this.validateResponse(this.physicalPersonQueryResponse)) {
	                this.isLoading = false;
	                this.formIsDisabled = false;
	                return;
	            }

	            if (this.newFreelancerRequestService.requestType !== 'Clave_Pin_SMS' && this.physicalPersonQueryResponse.consentUrlBoxed != null) {
	                this.updateTrustedUrl(this.physicalPersonQueryResponse.consentUrlBoxed);
	            }

	            this.isLoading = false;
	            if (environment.offlineMode || this.newFreelancerRequestService.requestType === 'Clave_Pin_SMS') {
	                this.newRequestStatus = { title: 'Request summary', step: 4 };
	            } else
	                this.newRequestStatus = { title: 'Validate request', step: 3 };
	        } catch (error) {
	            this.isLoading = false;
	            this.formIsDisabled = false;
	            if (error?.status === 422 || error?.error?.status === 422) {
	                this.toastService.showError('TOAST.ERROR', 'NEW_REQUEST.422_ERROR', true);
	                await this.adminMessagesComponent.setMessages();
	            } else if (error?.status === 400 && error.error === INVALID + SEPARATOR + DNI) {
	                this.toastService.showError('TOAST.ERROR', 'NEW_REQUEST.INVALID_DNI', true);
	            } else {
	                this.toastService.showError('TOAST.ERROR', 'NEW_REQUEST.ERROR', true);
	            }
	        }
	    }
	    else {
	        this.formIsDisabled = false;
	        this.checkValidationErrors();
	    }
	}

	private getSendLink(): boolean{
	    if (this.newFreelancerRequestService.requestType === 'Clave_Pin_SMS') {
	        return true;
	    }
	    return false;
	}

	checkValidationErrors(): void {
	    for (const key in this.newFreelancerRequestService.myForm.controls) { // LOOP THROUGH ALL FORM CONTROLS
	        if (Object.prototype.hasOwnProperty.call(this.newFreelancerRequestService.myForm.controls, key)) { // CHECK IF THE PROPERTY BELONGS TO THE OBJECT
	            const control = this.newFreelancerRequestService.myForm.get(key);
	            if (control.invalid) {
	                this.checkFieldIsRequiredOrInvalid(control, key);
	            }
	        }
	    }
	    this.showValidationErrors();

	    this.invalidFields = [];
	    this.requiredFieldsMissing = false;
	}

	checkFieldIsRequiredOrInvalid(control: AbstractControl, key: string): void {
	    if (control.errors['required']) {
	        if (!this.requiredFieldsMissing) this.requiredFieldsMissing = true; // SET THE FLAG TO TRUE ONLY ONCE
	        const formField = document.querySelector(`[formControlName="${key}"]`);
	        formField.classList.add('ng-dirty', 'ng-touched'); // ADD THE CLASSES TO SHOW THE ERROR
	    } else {
	        this.invalidFields.push(`NEW_REQUEST.INVALID_FORM.${key}`);  // ADD THE INVALID FIELD TO THE ARRAY
	    }
	}

	/**
	 * if a required field is not filled, it shows a toast with a message,
	 * otherwise it shows a toast with the invalid fields
	 */
	showValidationErrors(): void {
	    if (this.requiredFieldsMissing) {
	        this.toastService.showError('TOAST.INVALID', 'NEW_REQUEST.ALL_FIELDS_REQUIRED', false, null, 'newRequestErrors');
	    } else {
	        this.toastService.showError('TOAST.INVALID', 'NEW_REQUEST.INVALID_FORM_DETAIL', false, 5000, 'newRequestErrors', this.invalidFields);
	    }
	}

	goToLastStep(): void {
	    if (this.newFreelancerRequestService.isFinishingOTP){
	        this.newFreelancerRequestService.isFinishingOTP = false;
	        window.location.reload();
	    }
	    this.newRequestStatus = { title: 'Request summary', step: 4 };
	}


	validateResponse(physicalPersonQueryResponse: PhysicalPersonQueryResponse): boolean {
	    if (physicalPersonQueryResponse.statusCode == 400 || physicalPersonQueryResponse.queryId == null) {
	        this.toastService.showError(`Error ${physicalPersonQueryResponse.statusCode}`, `${physicalPersonQueryResponse?.formattedErrors}`, false, 10000);  //TODO: traudcir
	        return false;
	    }

	    return true;
	}

	isFieldInvalid(fieldName: string, errorName: string): boolean {
	    const field = this.newFreelancerRequestService.myForm.get(fieldName);

	    if (field.hasError(errorName) && (field.dirty || field.touched)) {
	        return true;
	    } else {
	        return false;
	    }
	}

	getRequestValidationTranslation(): string {
	    let requestValidationTranslation = 'NEW_REQUEST.SUBTITLE_VALIDATION';
	    if (this.newFreelancerRequestService.requestType === 'Clave_Pin') {
	        requestValidationTranslation += '_WIDGET';
	    }else if (this.newFreelancerRequestService.requestType === 'Clave_Pin_SMS') {
	        requestValidationTranslation += '_SMS';
	    }
	    return this.translateService.instant(requestValidationTranslation);
	}

	getRequestValidationInfo(): string {
	    if (this.newFreelancerRequestService.requestType === 'Clave_Pin_SMS') {
	        return this.translateService.instant('NEW_REQUEST.PENDING_SMS_VALIDATION');
	    }
	    return this.translateService.instant('NEW_REQUEST.VALIDATED');
	}
}
